// Lista de precios: ver/editar por CLIENTE o por LISTA, y crear listas nuevas.
// Modo Ver = imprimible con logo. Modo Editar = cambiar/agregar/quitar precios.
// Editar afecta a la LISTA (todos los clientes que la usan).

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { iconoTacho, iconoImpresora, iconoMas } from "../iconos";
import logoUrl from "../../img/logo-torremolinos.jpeg";
import type { Cliente, Lista, PreparaPedido, PrecioLista } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

interface ItemVista {
  id: number;
  codigo: number;
  descripcion: string;
  empaque: number;
  categoria_id: number;
  precio: number;
}
interface Vista {
  listaId: number;
  listaNombre: string;
  listaDesc: string | null;
  clienteNombre?: string;
  clienteCodigo?: string;
  categorias: Array<{ id: number; nombre: string }>;
  items: ItemVista[];
}

export async function renderListaPrecios(contenedor: HTMLElement): Promise<void> {
  let vista: Vista | null = null;
  let modo: "ver" | "editar" = "ver";
  const cambios = new Map<number, number>();
  const categoriasExtra = new Set<number>();

  contenedor.innerHTML = `
    <div class="no-print mb-4 flex flex-wrap items-end gap-3">
      <label class="min-w-[14rem] flex-1"><span class="etiqueta">Por cliente</span>
        <select id="cliente" class="campo"><option value="">Cargando...</option></select></label>
      <label class="min-w-[10rem]"><span class="etiqueta">o por lista</span>
        <select id="lista" class="campo"><option value="">Elegir lista...</option></select></label>
      <button id="nueva" class="btn-sec">${iconoMas} Nueva lista</button>
      <button id="modo" class="btn-sec" disabled>Editar</button>
      <button id="guardar" class="btn-primario hidden">Guardar cambios</button>
      <button id="imprimir" class="btn-sec" disabled>${iconoImpresora} Imprimir</button>
    </div>
    <div id="hoja"></div>
    <dialog id="dlgLista"></dialog>`;

  const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
  const selLista = contenedor.querySelector("#lista") as HTMLSelectElement;
  const btnNueva = contenedor.querySelector("#nueva") as HTMLButtonElement;
  const btnModo = contenedor.querySelector("#modo") as HTMLButtonElement;
  const btnGuardar = contenedor.querySelector("#guardar") as HTMLButtonElement;
  const btnImprimir = contenedor.querySelector("#imprimir") as HTMLButtonElement;
  const hoja = contenedor.querySelector("#hoja") as HTMLElement;
  const dlgLista = contenedor.querySelector("#dlgLista") as HTMLDialogElement;

  async function cargarSelects(): Promise<void> {
    try {
      const [clientes, listas] = await Promise.all([
        api.get<Cliente[]>("/api/clientes?activo=1"),
        api.get<Lista[]>("/api/listas"),
      ]);
      selCliente.innerHTML =
        `<option value="">Elegir cliente...</option>` +
        clientes.map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`).join("");
      selLista.innerHTML =
        `<option value="">Elegir lista...</option>` +
        listas
          .map((l) => `<option value="${l.id}">${esc(l.nombre)}${l.descripcion ? " — " + esc(l.descripcion) : ""}</option>`)
          .join("");
    } catch {
      selCliente.innerHTML = `<option value="">Error</option>`;
    }
  }

  const precioActual = (it: ItemVista): number =>
    cambios.has(it.id) ? (cambios.get(it.id) as number) : it.precio;

  function sincronizarBotones(): void {
    btnModo.disabled = !vista;
    btnImprimir.disabled = !vista || modo === "editar";
    btnModo.textContent = modo === "editar" ? "Ver" : "Editar";
    btnGuardar.classList.toggle("hidden", modo !== "editar" || cambios.size === 0);
  }

  function reset(): void {
    cambios.clear();
    categoriasExtra.clear();
    modo = "ver";
  }

  async function cargarPorCliente(id: number): Promise<void> {
    reset();
    selLista.value = "";
    hoja.innerHTML = `<div class="tarjeta text-masa-400">Cargando lista...</div>`;
    const prep = await api.get<PreparaPedido>(`/api/pedidos/preparar?cliente_id=${id}`);
    vista = {
      listaId: prep.lista.id,
      listaNombre: prep.lista.nombre,
      listaDesc: prep.lista.descripcion,
      clienteNombre: prep.cliente.nombre,
      clienteCodigo: prep.cliente.codigo,
      categorias: prep.categorias.map((c) => ({ id: c.id, nombre: c.nombre })),
      items: prep.items.map((it) => ({
        id: it.producto.id, codigo: it.producto.codigo, descripcion: it.producto.descripcion,
        empaque: it.producto.empaque, categoria_id: it.producto.categoria_id, precio: it.precio,
      })),
    };
    render();
  }

  async function cargarPorLista(id: number, nombre: string, desc: string | null): Promise<void> {
    reset();
    selCliente.value = "";
    hoja.innerHTML = `<div class="tarjeta text-masa-400">Cargando lista...</div>`;
    const filas = await api.get<PrecioLista[]>(`/api/listas/${id}/precios`);
    const catMap = new Map<number, string>();
    for (const f of filas) catMap.set(f.categoria_id, f.categoria);
    vista = {
      listaId: id, listaNombre: nombre, listaDesc: desc,
      categorias: [...catMap.entries()].map(([cid, n]) => ({ id: cid, nombre: n })),
      items: filas.map((f) => ({
        id: f.producto_id, codigo: f.codigo, descripcion: f.descripcion,
        empaque: f.empaque, categoria_id: f.categoria_id, precio: f.precio,
      })),
    };
    render();
  }

  selCliente.addEventListener("change", async () => {
    const id = Number(selCliente.value);
    if (!id) { vista = null; hoja.innerHTML = ""; sincronizarBotones(); return; }
    try { await cargarPorCliente(id); } catch (e) { hoja.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`; }
  });
  selLista.addEventListener("change", async () => {
    const id = Number(selLista.value);
    if (!id) { vista = null; hoja.innerHTML = ""; sincronizarBotones(); return; }
    const opt = selLista.options[selLista.selectedIndex].text;
    const [nombre, ...rest] = opt.split(" — ");
    try { await cargarPorLista(id, nombre, rest.join(" — ") || null); } catch (e) { hoja.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`; }
  });

  btnModo.addEventListener("click", () => { modo = modo === "editar" ? "ver" : "editar"; render(); });
  btnImprimir.addEventListener("click", () => window.print());

  btnGuardar.addEventListener("click", async () => {
    if (!vista || cambios.size === 0) return;
    const precios = [...cambios.entries()].map(([producto_id, precio]) => ({ producto_id, precio }));
    btnGuardar.disabled = true;
    try {
      await api.put(`/api/listas/${vista.listaId}/precios`, { precios });
      const id = vista.listaId, nom = vista.listaNombre, desc = vista.listaDesc;
      await cargarPorLista(id, nom, desc);
    } catch (e) {
      alert("No se pudo guardar: " + (e instanceof Error ? e.message : "error"));
    } finally {
      btnGuardar.disabled = false;
    }
  });

  btnNueva.addEventListener("click", () => {
    dlgLista.innerHTML = `
      <form method="dialog" class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-4 text-lg font-bold">Nueva lista de precios</h2>
        <div class="space-y-3">
          <label class="block"><span class="etiqueta">Nombre</span>
            <input id="l_nom" class="campo" placeholder="Lista31" /></label>
          <label class="block"><span class="etiqueta">Descripcion (opcional)</span>
            <input id="l_desc" class="campo" placeholder="Ej: MAYORISTA" /></label>
        </div>
        <p id="l_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="l_crear" value="ok" class="btn-primario">Crear y editar</button>
        </div>
      </form>`;
    const lErr = dlgLista.querySelector("#l_err") as HTMLElement;
    dlgLista.querySelector("#l_crear")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      lErr.textContent = "";
      const nom = (dlgLista.querySelector("#l_nom") as HTMLInputElement).value.trim();
      const desc = (dlgLista.querySelector("#l_desc") as HTMLInputElement).value.trim();
      if (!nom) { lErr.textContent = "Falta el nombre"; return; }
      try {
        const creada = await api.post<{ id: number; nombre: string; descripcion: string | null }>("/api/listas", { nombre: nom, descripcion: desc || null });
        await cargarSelects();
        dlgLista.close();
        await cargarPorLista(creada.id, creada.nombre, creada.descripcion);
        selLista.value = String(creada.id);
        modo = "editar";
        render();
      } catch (e) {
        lErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlgLista.showModal();
  });

  function render(): void {
    if (!vista) return;
    sincronizarBotones();
    modo === "ver" ? pintarVer(vista) : pintarEditar(vista);
  }

  function encabezado(v: Vista): string {
    const etiqueta = v.listaDesc ? ` — ${esc(v.listaDesc)}` : "";
    const sub = v.clienteNombre
      ? `<div>${esc(v.clienteNombre)} (${esc(v.clienteCodigo)})</div>`
      : "";
    return `
      <div class="mb-4 flex items-center justify-between gap-4">
        <img src="${logoUrl}" alt="Torremolinos" class="h-24 w-auto" />
        <div class="text-right text-sm">
          <div class="text-lg font-bold">Lista de precios</div>
          ${sub}
          <div class="text-masa-500">${esc(v.listaNombre)}${etiqueta}</div>
        </div>
      </div>`;
  }

  function grupos(v: Vista, soloConPrecio: boolean): Map<number, ItemVista[]> {
    const porCat = new Map<number, ItemVista[]>();
    for (const it of v.items) {
      if (soloConPrecio && it.precio <= 0) continue;
      const arr = porCat.get(it.categoria_id) ?? [];
      arr.push(it);
      porCat.set(it.categoria_id, arr);
    }
    return porCat;
  }

  function pintarVer(v: Vista): void {
    const nombreCat = new Map(v.categorias.map((c) => [c.id, c.nombre]));
    const porCat = grupos(v, true);
    const bloques: string[] = [];
    for (const [catId, items] of porCat) {
      const filas = items
        .map(
          (it) => `<tr class="border-t border-crema-100">
            <td class="py-1 pr-2 text-masa-500">${esc(it.codigo)}</td>
            <td class="py-1 pr-2">${esc(it.descripcion)}</td>
            <td class="py-1 pr-2 text-center">x${esc(it.empaque)}</td>
            <td class="py-1 text-right font-semibold tabular-nums">${formatearPesos(it.precio)}</td>
          </tr>`,
        )
        .join("");
      bloques.push(`
        <div class="mb-4 break-inside-avoid">
          <h3 class="mb-1 border-b-2 border-masa-600 text-sm font-bold uppercase tracking-wide text-masa-700">${esc(nombreCat.get(catId) ?? "Otros")}</h3>
          <table class="w-full text-sm">
            <thead class="text-xs text-masa-400"><tr>
              <th class="w-14 text-left font-normal">Cod</th><th class="text-left font-normal">Articulo</th>
              <th class="w-16 text-center font-normal">Emp</th><th class="w-24 text-right font-normal">Precio</th>
            </tr></thead>
            <tbody>${filas}</tbody>
          </table>
        </div>`);
    }
    hoja.innerHTML = `
      <div class="mx-auto max-w-2xl bg-white p-6 shadow-sm print:max-w-none print:p-0 print:shadow-none">
        ${encabezado(v)}
        <div class="columns-1 sm:columns-2 print:columns-2" style="column-gap:2rem">${bloques.join("") || `<p class="text-masa-400">Lista vacia.</p>`}</div>
      </div>`;
  }

  function pintarEditar(v: Vista): void {
    const nombreCat = new Map(v.categorias.map((c) => [c.id, c.nombre]));
    const porCat = grupos(v, false);
    const bloques: string[] = [];
    const sinArticulos: number[] = [];

    for (const cat of v.categorias) {
      const items = porCat.get(cat.id) ?? [];
      const visibles = items.filter((it) => precioActual(it) > 0);
      if (visibles.length === 0 && !categoriasExtra.has(cat.id)) {
        if (items.length > 0) sinArticulos.push(cat.id);
        continue;
      }
      const filas = visibles
        .map((it) => {
          const cambiado = cambios.has(it.id);
          return `<tr class="border-t border-crema-100">
            <td class="py-1 pr-2 text-masa-500">${esc(it.codigo)}</td>
            <td class="py-1 pr-2">${esc(it.descripcion)} <span class="text-xs text-masa-400">x${esc(it.empaque)}</span></td>
            <td class="py-1 pr-1 text-right"><input class="precio-input ${cambiado ? "precio-override" : ""}" type="number" min="0" step="1" data-precio-edit="${it.id}" value="${precioActual(it)}" /></td>
            <td class="py-1 text-right"><button class="chip-accion" data-quitar="${it.id}" title="Quitar de la lista">${iconoTacho}</button></td>
          </tr>`;
        })
        .join("");
      const ceros = items.filter((it) => precioActual(it) <= 0);
      const addRow = ceros.length
        ? `<tr class="border-t border-dashed border-crema-300">
            <td colspan="2" class="py-1 pr-2">
              <select class="campo py-1 text-sm" data-add-sel="${cat.id}">
                <option value="">Agregar articulo...</option>
                ${ceros.map((it) => `<option value="${it.id}">${esc(it.codigo)} — ${esc(it.descripcion)}</option>`).join("")}
              </select></td>
            <td class="py-1 pr-1 text-right"><input class="precio-input" type="number" min="0" step="1" data-add-precio="${cat.id}" placeholder="0" /></td>
            <td class="py-1 text-right"><button class="chip-accion" data-add-cat="${cat.id}" title="Agregar">${iconoMas}</button></td>
          </tr>`
        : "";
      bloques.push(`
        <div class="mb-4 break-inside-avoid">
          <h3 class="mb-1 border-b-2 border-masa-600 text-sm font-bold uppercase tracking-wide text-masa-700">${esc(nombreCat.get(cat.id) ?? "Otros")}</h3>
          <table class="w-full text-sm"><tbody>${filas}${addRow}</tbody></table>
        </div>`);
    }

    let extra = "";
    if (sinArticulos.length) {
      extra = `
        <div class="mb-4 flex break-inside-avoid flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-crema-300 p-4 text-center">
          <span class="text-masa-500">${iconoMas}</span>
          <div class="text-sm font-semibold text-masa-500">Anadir categoria</div>
          <div class="flex flex-wrap justify-center gap-1">
            ${sinArticulos.map((id) => `<button class="btn-sec px-2 py-1 text-xs" data-add-categoria="${id}">${esc(nombreCat.get(id) ?? "")}</button>`).join("")}
          </div>
        </div>`;
    }

    hoja.innerHTML = `
      <div class="mb-3 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">
        Editando <b>${esc(v.listaNombre)}${v.listaDesc ? " — " + esc(v.listaDesc) : ""}</b>: afecta a <b>todos</b> los clientes con esta lista.
      </div>
      <div class="tarjeta">
        ${encabezado(v)}
        <div class="columns-1 sm:columns-2" style="column-gap:2rem">${bloques.join("") || `<p class="text-masa-400">Lista vacia. Agrega articulos desde cada categoria.</p>`}${extra}</div>
      </div>`;
  }

  hoja.addEventListener("input", (ev) => {
    const t = ev.target as HTMLElement;
    if (t.matches("[data-precio-edit]")) {
      const inp = t as HTMLInputElement;
      const pid = Number(inp.dataset.precioEdit);
      const it = vista?.items.find((i) => i.id === pid);
      if (!it) return;
      const val = Math.max(0, Number(inp.value) || 0);
      if (val === it.precio) cambios.delete(pid);
      else cambios.set(pid, val);
      inp.classList.toggle("precio-override", cambios.has(pid));
      sincronizarBotones();
    }
  });

  hoja.addEventListener("click", (ev) => {
    const quitar = (ev.target as HTMLElement).closest("[data-quitar]");
    if (quitar) {
      const pid = Number(quitar.getAttribute("data-quitar"));
      if (confirm("Quitar este articulo de la lista (precio 0)?")) { cambios.set(pid, 0); render(); }
      return;
    }
    const add = (ev.target as HTMLElement).closest("[data-add-cat]");
    if (add) {
      const catId = add.getAttribute("data-add-cat");
      const sel = hoja.querySelector(`[data-add-sel="${catId}"]`) as HTMLSelectElement | null;
      const precioInp = hoja.querySelector(`[data-add-precio="${catId}"]`) as HTMLInputElement | null;
      const pid = Number(sel?.value);
      const precio = Math.max(0, Number(precioInp?.value) || 0);
      if (!pid) { alert("Elegi un articulo."); return; }
      if (precio <= 0) { alert("Ingresa un precio mayor a 0."); return; }
      cambios.set(pid, precio);
      render();
      return;
    }
    const addCat = (ev.target as HTMLElement).closest("[data-add-categoria]");
    if (addCat) { categoriasExtra.add(Number(addCat.getAttribute("data-add-categoria"))); render(); }
  });

  await cargarSelects();
}
